import type { KeyboardEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { Tab, Tabs } from '@vd/ui';

export type WalkthroughTab = 'recorder' | 'history';

const TABS: Array<{ id: WalkthroughTab; label: string; path: string }> = [
  { id: 'recorder', label: 'Recorder', path: '/recordings' },
  { id: 'history', label: 'History', path: '/recordings/history' },
];

/** The id of the panel a tab controls, shared by the tab and the panel. */
export const WALKTHROUGH_PANEL_ID = 'walkthrough-tab-panel';

/**
 * The recorder page's two views: recording one, and the ones already made (Jason,
 * 24 Sep).
 *
 * Each has its own address, so the review page's "All walkthroughs" comes straight
 * back to the history and a reload stays where it was. The design system's tabs, with
 * the keyboard a tab list owes: Left and Right move between them, Enter and Space
 * choose the one with focus.
 */
export function WalkthroughTabs({ active }: { active: WalkthroughTab }) {
  const navigate = useNavigate();
  const go = (tab: WalkthroughTab) => {
    if (tab === active) return;
    navigate(TABS.find((entry) => entry.id === tab)!.path);
  };
  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
    event.preventDefault();
    const at = TABS.findIndex((entry) => entry.id === active);
    const next = TABS[(at + (event.key === 'ArrowRight' ? 1 : -1) + TABS.length) % TABS.length]!;
    const tablist = event.currentTarget;
    go(next.id);
    // Focus follows once the tab is the active one, which is when it can take it.
    requestAnimationFrame(() => tablist.querySelector<HTMLElement>(`#walkthrough-tab-${next.id}`)?.focus());
  };
  return (
    <Tabs aria-label="Video recordings" onKeyDown={onKeyDown}>
      {TABS.map((tab) => (
        <Tab
          key={tab.id}
          id={`walkthrough-tab-${tab.id}`}
          active={tab.id === active}
          aria-controls={WALKTHROUGH_PANEL_ID}
          onClick={() => go(tab.id)}
          onKeyDown={(event) => {
            if (event.key !== 'Enter' && event.key !== ' ') return;
            event.preventDefault();
            go(tab.id);
          }}
        >
          {tab.label}
        </Tab>
      ))}
    </Tabs>
  );
}
